import React from "react";
import { Button } from "react-bootstrap";
import {
  useV2ListNotificationsQuery,
  useV2NotificationsMarkAllReadMutation,
} from "@generated";
import useMarkNotificationRead from "@common/hooks/useMarkNotificationRead";
import NotificationRow from "@components/NotificationRow";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";

interface NotificationDropdownProps {
  // "left" anchors the panel's left edge to its positioned ancestor (for react nav);
  align?: "left" | "right";
  // Full style override/merge — used by callers that compute their own viewport position
  style?: React.CSSProperties;
  refetchUnreadCount: () => void;
}

const NotificationDropdown = React.forwardRef<
  HTMLDivElement,
  NotificationDropdownProps
>(({ align = "right", style, refetchUnreadCount }, ref) => {
  const { data, isLoading, refetch } = useV2ListNotificationsQuery({
    page: { size: 5 },
  });
  const [markAllRead] = useV2NotificationsMarkAllReadMutation();
  const handleView = useMarkNotificationRead();
  const notifications = data?.data ?? [];

  const defaultPositionStyle: React.CSSProperties = {
    position: "absolute",
    top: "100%",
    marginTop: 8,
    ...(align === "right" ? { right: 0 } : { left: 0 }),
  };

  const handleClearAll = async () => {
    await markAllRead().unwrap();
    refetch();
    refetchUnreadCount();
  };

  return (
    <div
      ref={ref}
      data-notification-panel
      style={{
        ...defaultPositionStyle,
        width: 340,
        maxWidth: "90vw",
        backgroundColor: "#fff",
        borderRadius: 6,
        border: "1px solid #e0e0e0",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        overflow: "hidden",
        zIndex: 1000,
        ...style,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Button
          className="btnSeafoam"
          href="/notifications"
          style={{ color: "white !important" }}
        >
          View All
        </Button>
        <button
          type="button"
          onClick={handleClearAll}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            color: "#418172",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Clear All
        </button>
      </div>
      {isLoading ? (
        <SmallSpinner />
      ) : notifications.length === 0 ? (
        <div style={{ padding: 16, color: "#6c757d", fontSize: 14 }}>
          No notifications
        </div>
      ) : (
        notifications.map((notification) => (
          <NotificationRow
            key={notification.id}
            id={notification.id}
            body={notification.attributes.body}
            link={notification.attributes.link}
            readAt={notification.attributes["read-at"]}
            createdAt={notification.attributes["created-at"]}
            onView={handleView}
          />
        ))
      )}
    </div>
  );
});

NotificationDropdown.displayName = "NotificationDropdown";

export default NotificationDropdown;
